import { Signal } from "../core/Signal.js";
import { KeyCode } from "../util/KeyCode.js";
import { object } from "../util/ObjectUtil.js";
import { Actor, ActorNonOptions, Pointer, ActorOptions } from "./Actor.js";

export interface ClickOptions extends ActorOptions {
    maxDistance?: number;
    maxLifetime?: number;
    doubleClick?: boolean;
    doubleClickTime?: number;
    maxDoubleClickDistance?: number;
    repeatInterval?: number;
    repeatStartTime?: number;
    keyboardSupport?: boolean;
}

export class PointerInfoClick extends Pointer {
    isClick: boolean = false;
    isDoubleClick: boolean = false;
}

export class Click extends Actor<PointerInfoClick, ClickOptions> implements ClickOptions {
    public static DEFAULT: ClickOptions = {
        maxDistance: 30,
        maxDoubleClickDistance: 30,
        maxLifetime: 1200,
        // maxPointers: 1,
        doubleClick: false,
        doubleClickTime: 400,
        repeatInterval: 200,
        repeatStartTime: 500,
        keyboardSupport: true
    };

    public onClick = Signal.create<[PointerInfoClick, PointerInfoClick[], Click]>();
    public onDoubleClick = Signal.create<[PointerInfoClick, PointerInfoClick[], Click]>();
    public onRepeat = Signal.create<[number, Click]>();

    public maxLifetime: number;
    public maxDistance: number;
    public doubleClick: boolean;
    public doubleClickTime: number;
    public maxDoubleClickDistance: number;
    public repeatInterval: number;
    public repeatStartTime: number;
    public keyboardSupport: boolean;

    private _onKeyUp: (e: KeyboardEvent) => void = null;

    constructor(element: HTMLElement = null, options: ClickOptions = null) {

        super(null, ActorNonOptions);

        this._onKeyUp = (e) => this.onKeyDown(e);

        if (options !== ActorNonOptions)
            this.setOptions(options);

        this.element = element;

        let repeatTimeout: any = 0;
        let repeatCount: any = 0;
        let maxPointerCount = 0;

        var triggerRepeat = () => {
            if(!this.onRepeat){
                clearTimeout(repeatTimeout);
                return;
            }
            this.onRepeat.dispatch(++repeatCount, this);

            clearTimeout(repeatTimeout);

            var time = this.repeatStartTime;
            if (repeatCount > 1){
                time = this.repeatInterval;
            }
            repeatTimeout = setTimeout(() => triggerRepeat(), time);
        };

        this.onDown.add((pointers) => {
            maxPointerCount++;

            if (this.pointers.length > 1)
                return;
            if (repeatCount == 0)
                triggerRepeat();
        });

        this.onUp.add((pointers) => {
            if (this.pointers.length > 0)
                return;
            clearInterval(repeatTimeout);
            repeatCount = 0;
        });

        this.onClear.add((pointers) => {
            maxPointerCount = 0;
        });

        var lastClickX = NaN;
        var lastClickY = NaN;
        var lastClickTime = NaN;
        var clickTimeout: any = 0;

        this.onUp.add((pointers) => {
            if (this.count !== 0)
                return;

            /**
             * Sollten zuvor mehrere Pointer parallell
             * auf Element gewesen sein, werden diese Pointer ignoriert.
             * Somit wird verhindert, dass bspw. bei einer Pinchgeste der letzte
             * Pointer als Klick interpretiert wird.
             */
            if (maxPointerCount > 1) {
                lastClickX = NaN;
                return;
            }

            var lifeTime = Actor.lifetime(pointers);
            var pointer = pointers[0];

            if (!(pointer.distance <= this.maxDistance && lifeTime <= this.maxLifetime))
                return;

            if (this.doubleClick) {
                if (isNaN(lastClickX)) {
                    lastClickX = pointer.x;
                    lastClickY = pointer.y;
                    lastClickTime = (new Date()).getTime();

                    clickTimeout = setTimeout(() => {
                        var p = pointer;
                        var pts = pointers;
                        this.onClick.dispatch(p, pts, this);
                        lastClickX = NaN;
                    }, this.doubleClickTime + 5);
                }
                else {
                    var dx = pointer.x - lastClickX;
                    var dy = pointer.y - lastClickY;
                    var distance = Math.sqrt(dx * dx + dy * dy);
                    var deltaTime = (new Date()).getTime() - lastClickTime;

                    clearTimeout(clickTimeout);

                    if (deltaTime <= this.doubleClickTime && distance < this.maxDoubleClickDistance) {
                        for (var pointer of pointers)
                            pointer.isDoubleClick = true;
                        this.pointer.isDoubleClick = true;
                        this.onDoubleClick.dispatch(pointer, pointers, this);
                    } else {
                        this.onClick.dispatch(pointer, pointers, this);
                    }

                    lastClickX = NaN;
                }
            }
            else {
                pointer.isClick = true;
                this.onClick.dispatch(pointer, pointers, this);
            }
        });
    }

    protected createPointer(): PointerInfoClick {
        return new PointerInfoClick();
    }


    protected onChangeElement(newElement: HTMLElement, oldElement: HTMLElement): void {
        if (oldElement) {
            oldElement.removeEventListener("keydown", this._onKeyUp);
        }

        if (newElement) {
            newElement.addEventListener("keydown", this._onKeyUp);
        }
    }

    private onKeyDown(e: KeyboardEvent): void {
        // console.log("AHHH", e.keyCode);
        if (KeyCode.is(e, KeyCode.ENTER, KeyCode.SPACE)) {
            this.onClick.dispatch(this.pointer, this.pointers, this);
            this.onRepeat.dispatch(1, this);
        }
    }

    public setOptions(options: ClickOptions) {
        options = object.merge({}, Click.DEFAULT, options);
        this.maxDistance = options.maxDistance;
        this.maxLifetime = options.maxLifetime;
        this.doubleClick = options.doubleClick;
        this.doubleClickTime = options.doubleClickTime;
        this.maxDoubleClickDistance = options.maxDoubleClickDistance;
        this.repeatInterval = options.repeatInterval;
        this.repeatStartTime = options.repeatStartTime;
        this.keyboardSupport = options.keyboardSupport;
        super.setOptions(options);
    }

    public dispose(): void {
        this.onClick.dispose();
        this.onClick = null;
        this.onDoubleClick.dispose();
        this.onDoubleClick = null;
        this.onRepeat.dispose();
        this.onRepeat = null;
        super.dispose();
    }
}